/*
 * Monitor production service-worker updates. Activation is shared across tabs,
 * but reloading always requires consent in this document. Never read auth state.
 */
export interface AppUpdateState {
  available: boolean;
  refreshing: boolean;
  error: string | null;
}

const CHECK_INTERVAL = 5 * 60 * 1000;
const ACTIVATION_TIMEOUT = 15 * 1000;

export function monitorAppUpdates(
  onChange: (state: AppUpdateState) => void,
  reload: () => void = () => window.location.reload(),
) {
  let registration: ServiceWorkerRegistration | undefined;
  let disposed = false;
  let refreshRequested = false;
  let activationTarget: ServiceWorker | undefined;
  let controlled = Boolean(navigator.serviceWorker?.controller);
  let checking = false;
  let activationTimer: ReturnType<typeof setTimeout> | undefined;
  let interval: ReturnType<typeof setInterval> | undefined;
  const workers = new Map<ServiceWorker, () => void>();
  let state: AppUpdateState = { available: false, refreshing: false, error: null };

  const publish = (changes: Partial<AppUpdateState>) => {
    state = { ...state, ...changes };
    if (!disposed) onChange(state);
  };
  const clearActivationTimer = () => clearTimeout(activationTimer);
  const completeRefresh = () => {
    if (disposed || !refreshRequested) return;
    clearActivationTimer();
    refreshRequested = false;
    activationTarget = undefined;
    reload();
  };
  const failedActivation = () => {
    clearActivationTimer();
    refreshRequested = false;
    activationTarget = undefined;
    publish({ refreshing: false, error: 'Unable to refresh. Please try again.' });
  };
  const controllerChanged = () => {
    if (disposed) return;
    if (refreshRequested) {
      completeRefresh();
    } else if (controlled) {
      publish({ available: true });
    }
    controlled = Boolean(navigator.serviceWorker.controller);
  };
  const inspectWaiting = () => {
    if (registration?.waiting && navigator.serviceWorker.controller) {
      publish({ available: true });
    }
  };
  const trackWorker = (worker: ServiceWorker | null | undefined) => {
    if (!worker || workers.has(worker)) return;
    const changed = () => {
      if (worker.state === 'installed') inspectWaiting();
      if (worker.state === 'activated' && worker === activationTarget && refreshRequested) completeRefresh();
      if (worker.state === 'redundant' && worker === activationTarget && refreshRequested) failedActivation();
    };
    workers.set(worker, changed);
    worker.addEventListener('statechange', changed);
    changed();
  };
  const trackInstalling = () => trackWorker(registration?.installing);
  const check = async () => {
    if (disposed || !registration || checking || !navigator.onLine || document.visibilityState !== 'visible') return;
    checking = true;
    try {
      await registration.update();
      if (!disposed) inspectWaiting();
    } catch {
      // Offline/transient deployment failures must not interrupt the CRM.
    } finally {
      checking = false;
    }
  };

  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.addEventListener('controllerchange', controllerChanged);
    document.addEventListener('visibilitychange', check);
    window.addEventListener('online', check);
    void navigator.serviceWorker.register(`${import.meta.env.BASE_URL}sw.js`, {
      updateViaCache: 'none',
    }).then((registered) => {
      if (disposed) return;
      registration = registered;
      registration.addEventListener('updatefound', trackInstalling);
      trackInstalling();
      inspectWaiting();
      interval = setInterval(check, CHECK_INTERVAL);
      void check();
    }).catch(() => {
      // The application remains usable when worker registration is unavailable.
    });
  }

  return {
    refresh() {
      if (disposed || !state.available || state.refreshing) return;
      publish({ refreshing: true, error: null });
      refreshRequested = true;
      const worker = registration?.waiting;
      if (!worker) {
        // Another tab may already have activated the update.
        completeRefresh();
        return;
      }
      activationTarget = worker;
      activationTimer = setTimeout(() => {
        // Lifecycle events can arrive after this timer in a throttled tab.
        if (worker.state === 'activated' || navigator.serviceWorker.controller === worker) completeRefresh();
        else failedActivation();
      }, ACTIVATION_TIMEOUT);
      // A worker already waiting when this tab opened has no updatefound listener.
      trackWorker(worker);
      if (!refreshRequested) return;
      try {
        worker.postMessage({ type: 'SKIP_WAITING' });
      } catch {
        failedActivation();
      }
    },
    dispose() {
      disposed = true;
      clearActivationTimer();
      clearInterval(interval);
      navigator.serviceWorker?.removeEventListener('controllerchange', controllerChanged);
      registration?.removeEventListener('updatefound', trackInstalling);
      workers.forEach((listener, worker) => worker.removeEventListener('statechange', listener));
      document.removeEventListener('visibilitychange', check);
      window.removeEventListener('online', check);
    },
  };
}
